<template>
  <v-container fluid>
    <v-row>
      <v-col cols="12">
        <v-card>
          <v-card-title class="d-flex align-center">
            <v-icon left class="mr-2">mdi-account-tie</v-icon>
            Registro de postulaciones - Proceso especial por Artículo 88
            <v-spacer />
            <v-btn
              v-if="puedeCrear"
              color="primary"
              :to="{ name: 'incentivos-retiro-certificado-crear' }"
            >
              <v-icon left>mdi-plus</v-icon>
              Nuevo Proceso de Postulaciones
            </v-btn>
          </v-card-title>

          <!-- Filtros -->
          <v-card-text>
            <v-row>
              <v-col cols="12" md="3">
                <v-select
                  v-model="filtros.estado"
                  :items="estadosDisponibles"
                  label="Estado"
                  clearable
                  outlined
                  dense
                  hide-details
                  @change="cargarCertificados"
                />
              </v-col>
              <v-col v-if="esOperador" cols="12" md="4">
                <v-autocomplete
                  v-model="filtros.municipalidad_id"
                  :items="municipalidades"
                  item-text="nombre"
                  item-value="id"
                  label="Municipalidad"
                  clearable
                  outlined
                  dense
                  hide-details
                  @change="cargarCertificados"
                />
              </v-col>
              <v-col cols="12" md="2">
                <v-text-field
                  v-model="filtros.fecha_desde"
                  type="date"
                  label="Desde"
                  outlined
                  dense
                  hide-details
                  @change="cargarCertificados"
                />
              </v-col>
              <v-col cols="12" md="2">
                <v-text-field
                  v-model="filtros.fecha_hasta"
                  type="date"
                  label="Hasta"
                  outlined
                  dense
                  hide-details
                  @change="cargarCertificados"
                />
              </v-col>
            </v-row>
          </v-card-text>

          <v-divider />

          <!-- Tabla de certificados -->
          <v-data-table
            :headers="headers"
            :items="certificados"
            :loading="cargando"
            :server-items-length="totalCertificados"
            :options.sync="opciones"
            :footer-props="{
              'items-per-page-options': [15, 30, 50],
            }"
            class="elevation-0"
            @update:options="cargarCertificados"
          >
            <!-- Número de certificado -->
            <template #[`item.numero_certificado`]="{ item }">
              <router-link
                :to="{
                  name: 'incentivos-retiro-certificado-detalle',
                  params: { id: item.id },
                }"
                class="text-decoration-none"
              >
                <strong>{{ item.numero_certificado }}</strong>
              </router-link>
            </template>

            <!-- Tipo de ley -->
            <template #[`item.tipo`]="{ item }">
              <v-chip
                :color="getTipoLeyColor(item.tipo)"
                x-small
              >
                {{ getTipoLeyTexto(item.tipo) }}
              </v-chip>
            </template>

            <!-- Estado -->
            <template #[`item.estado`]="{ item }">
              <v-chip
                :color="getEstadoColor(item.estado)"
                small
                dark
              >
                {{ getEstadoTexto(item.estado) }}
              </v-chip>
            </template>

            <!-- Postulaciones -->
            <template #[`item.postulaciones_count`]="{ item }">
              <v-tooltip bottom>
                <template #activator="{ on }">
                  <v-chip small v-on="on">
                    {{ item.postulaciones ? item.postulaciones.length : 0 }}
                  </v-chip>
                </template>
                <span>Total de postulaciones</span>
              </v-tooltip>
            </template>

            <!-- Postulaciones por estado -->
            <template #[`item.postulaciones_resumen`]="{ item }">
              <div class="d-flex gap-1">
                <v-chip
                  v-if="getPostulacionesPorEstado(item, 'pendiente') > 0"
                  x-small
                  color="orange"
                  dark
                >
                  <v-icon x-small left>mdi-clock-outline</v-icon>
                  {{ getPostulacionesPorEstado(item, 'pendiente') }}
                </v-chip>
                <v-chip
                  v-if="getPostulacionesPorEstado(item, 'aprobado') > 0"
                  x-small
                  color="green"
                  dark
                >
                  <v-icon x-small left>mdi-check</v-icon>
                  {{ getPostulacionesPorEstado(item, 'aprobado') }}
                </v-chip>
                <v-chip
                  v-if="getPostulacionesPorEstado(item, 'observado') > 0"
                  x-small
                  color="red"
                  dark
                >
                  <v-icon x-small left>mdi-alert</v-icon>
                  {{ getPostulacionesPorEstado(item, 'observado') }}
                </v-chip>
              </div>
            </template>

            <!-- Fecha creación -->
            <template #[`item.fecha_creacion`]="{ item }">
              {{ formatearFecha(item.fecha_creacion) }}
            </template>

            <!-- Acciones -->
            <template #[`item.acciones`]="{ item }">
              <div class="d-flex justify-start mx-auto" style="width: 50px;">
                <v-btn
                  icon
                  small
                  :to="{
                    name: 'incentivos-retiro-certificado-detalle',
                    params: { id: item.id },
                  }"
                >
                  <v-icon small>mdi-eye</v-icon>
                </v-btn>
                <v-btn
                  v-if="item.estado === 'borrador' && puedeEditar"
                  icon
                  small
                  color="error"
                  @click="confirmarEliminar(item)"
                >
                  <v-icon small>mdi-delete</v-icon>
                </v-btn>
              </div>
            </template>

            <!-- Sin datos -->
            <template #no-data>
              <div class="text-center py-5">
                <v-icon size="64" color="grey lighten-2">
                  mdi-file-document-outline
                </v-icon>
                <p class="text-h6 grey--text mt-3">
                  No hay certificados registrados
                </p>
              </div>
            </template>
          </v-data-table>
        </v-card>
      </v-col>
    </v-row>

    <!-- Diálogo de confirmación eliminar -->
    <v-dialog v-model="dialogEliminar" max-width="500">
      <v-card>
        <v-card-title class="text-h5">
          ¿Eliminar certificado?
        </v-card-title>
        <v-card-text>
          ¿Está seguro que desea eliminar el certificado
          <strong>{{ certificadoAEliminar?.numero_certificado }}</strong>?
          Esta acción no se puede deshacer.
        </v-card-text>
        <v-card-actions>
          <v-spacer />
          <v-btn text @click="dialogEliminar = false">
            Cancelar
          </v-btn>
          <v-btn color="error" text :loading="eliminando" @click="eliminarCertificado">
            Eliminar
          </v-btn>
        </v-card-actions>
      </v-card>
    </v-dialog>
  </v-container>
</template>

<script>
import { apiClient } from '@/admin/services/API'
import { format, parseISO } from 'date-fns'
import es from 'date-fns/locale/es'
import { notificar, notificarErrorResponse } from '@/admin/utils/notificar'

export default {
  name: 'IncentivosRetiroCertificadosListado',
  data() {
    return {
      certificados: [],
      cargando: false,
      totalCertificados: 0,
      opciones: {},
      filtros: {
        estado: null,
        municipalidad_id: null,
        fecha_desde: null,
        fecha_hasta: null,
      },
      municipalidades: [],
      dialogEliminar: false,
      certificadoAEliminar: null,
      eliminando: false,
    }
  },
  computed: {
    headers() {
      const baseHeaders = [
        { text: 'N° Certificado', value: 'numero_certificado', sortable: false },
        { text: 'Tipo', value: 'tipo', sortable: false },
        { text: 'Estado', value: 'estado', sortable: false },
        { text: 'Total', value: 'postulaciones_count', sortable: false },
        { text: 'Estado postulaciones', value: 'postulaciones_resumen', sortable: false },
        { text: 'Fecha creación', value: 'fecha_creacion', sortable: true },
        { text: 'Acciones', value: 'acciones', sortable: false, align: 'center' },
      ]

      // Si es operador, mostrar municipalidad
      if (this.esOperador) {
        baseHeaders.splice(1, 0, {
          text: 'Municipalidad',
          value: 'municipalidad.nombre',
          sortable: false,
        })
      }

      return baseHeaders
    },
    estadosDisponibles() {
      return [
        { text: 'Borrador', value: 'borrador' },
        { text: 'Firmado/Enviado', value: 'firmado_enviado' },
        { text: 'Resuelto', value: 'resuelto' },
      ]
    },
    esOperador() {
      return this.$auth.can(['gestor-subdere-incentivo-retiro'])
    },
    puedeCrear() {
      return this.$auth.can(['gestor-municipalidad-incentivo-retiro'])
    },
    puedeEditar() {
      return this.$auth.can(['gestor-municipalidad-incentivo-retiro'])
    },
  },
  mounted() {
    this.cargarCertificados()
    if (this.esOperador) {
      this.cargarMunicipalidades()
    }
  },
  methods: {
    async cargarCertificados() {
      this.cargando = true
      try {
        const { page = 1, itemsPerPage = 15 } = this.opciones
        const params = {
          page,
          per_page: itemsPerPage,
          ...this.filtros,
        }

        const { data } = await apiClient.get('/tramites/incentivos-retiro/certificados', { params })
        
        this.certificados = data.data
        this.totalCertificados = data.total
      } catch (error) {
        notificarErrorResponse(error, 'Error al cargar certificados')
        console.error(error)
      } finally {
        this.cargando = false
      }
    },
    async cargarMunicipalidades() {
      try {
        const { data } = await apiClient.get('usuarios/instituciones', { params: { tipo: 2 } })
        this.municipalidades = data.data || []
      } catch (error) {
        console.error('Error al cargar municipalidades:', error)
      }
    },
    confirmarEliminar(certificado) {
      this.certificadoAEliminar = certificado
      this.dialogEliminar = true
    },
    async eliminarCertificado() {
      this.eliminando = true
      try {
        await apiClient.delete(`/tramites/incentivos-retiro/certificados/${this.certificadoAEliminar.id}`)
        notificar({ message: 'Certificado eliminado exitosamente', severity: 'success' })
        this.dialogEliminar = false
        this.cargarCertificados()
      } catch (error) {
        notificarErrorResponse(error, 'Error al eliminar el certificado')
        console.error(error)
      } finally {
        this.eliminando = false
      }
    },
    getEstadoColor(estado) {
      const colores = {
        borrador: 'grey',
        firmado_enviado: 'blue',
        resuelto: 'green',
      }
      return colores[estado] || 'grey'
    },
    getEstadoTexto(estado) {
      const textos = {
        borrador: 'Borrador',
        firmado_enviado: 'Firmado/Enviado',
        resuelto: 'Resuelto',
      }
      return textos[estado] || estado
    },
    getTipoLeyColor(tipo) {
      const colores = {
        ley_21135: 'info',
        ley_21724_menor_75: 'info',
        ley_21724_mayor_75: 'info',
      }
      return colores[tipo] || 'grey'
    },
    getTipoLeyTexto(tipo) {
      const textos = {
        ley_21135: '21.135',
        ley_21724_menor_75: '21.724 (<75)',
        ley_21724_mayor_75: '21.724 (≥75)',
      }
      return textos[tipo] || tipo
    },
    getPostulacionesPorEstado(certificado, estado) {
      if (!certificado.postulaciones) return 0
      return certificado.postulaciones.filter((p) => p.estado === estado).length
    },
    formatearFecha(fecha) {
      console.log(fecha);
      
      if (!fecha) return '-'
      try {
        return format(parseISO(fecha), "dd/MM/yyyy HH:mm:ss", { locale: es })
      } catch {
        if (typeof fecha === 'string' && fecha.length >= 19) {
          return fecha.slice(0, 19).replace('T', ' ')
        }
        return fecha
      }
    },
  },
}
</script>

<style scoped>
.gap-1 {
  gap: 4px;
}
</style>
